import { useState, Fragment } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useOdooAuth } from '@/lib/OdooAuthContext';
import { useRepProfile } from '@/lib/RepProfileContext';

import CustomerSelector from '@/components/orders/CustomerSelector';
import ProductPicker from '@/components/orders/ProductPicker';
import OrderReview from '@/components/orders/OrderReview';
import PromoPicker from '@/components/orders/PromoPicker';
import { api, ApiError, getBackend } from '@/api/apiClient';
import { enqueue } from '@/lib/outbox';
import { notifyEnqueued } from '@/lib/syncEngine';
import { cur } from '@/lib/currency';
import { todayLocalISO } from '@/lib/dates';
import { formatDate } from '@/lib/companyConfig';

const STEPS = ['اختيار العميل', 'إضافة منتجات', 'مراجعة وتأكيد'];

export default function NewOrder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { odooSession, getCredential } = useOdooAuth();
  const { profile: repProfile, isManager } = useRepProfile();

  const preCustomerId = searchParams.get('customer_id');
  const preCustomerName = searchParams.get('customer_name');

  const [step, setStep] = useState(preCustomerId ? 2 : 1);
  const [showPromos, setShowPromos] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(
    preCustomerId ? { id: parseInt(preCustomerId), name: preCustomerName || '' } : null
  );
  const [customerInfo, setCustomerInfo] = useState(null);
  const [orderLines, setOrderLines] = useState([]);
  const [priceMap, setPriceMap] = useState({});
  const [notes, setNotes] = useState('');
  // SellGo's own database (not Odoo): records cash vs credit (آجل) on the order,
  // and replaces a draft whose confirmation it refused. Odoo orders stay as they were.
  const sellgoDb = getBackend() === 'supabase';
  // Managers and supervisors may date the invoice back or ahead; a rep's is the
  // day it's written (kept when it syncs later, offline).
  const canPickDate = sellgoDb && ['manager', 'supervisor'].includes(odooSession?.role);
  const [invoiceDate, setInvoiceDate] = useState(todayLocalISO);
  const [paymentType, setPaymentType] = useState('credit');
  const [refusedDraftId, setRefusedDraftId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [successOrder, setSuccessOrder] = useState(null);

  // ── Customer selection ───────────────────────────────────────────────
  const handleSelectCustomer = async (customer, info) => {
    setSelectedCustomer(customer);
    setCustomerInfo(info);

    if (customer) {
      // Fetch pricelist prices for any already-in-cart products
      const templateIds = orderLines.map(l => l.template_id);
      if (templateIds.length > 0) {
        api.post('/products/pricelist', {
          partner_id: customer.id, product_ids: templateIds
        }).then(r => { if (r?.success) setPriceMap(r.data || {}); });
      }
    } else {
      setCustomerInfo(null);
    }
  };

  const handleCustomerContinue = async () => {
    // When moving to products step, fetch pricelist prices
    if (selectedCustomer?.id) {
      api.post('/products/pricelist', {
        partner_id: selectedCustomer.id, product_ids: []
      }).then(r => { if (r?.success) setPriceMap(r.data || {}); });
    }
    setStep(2);
  };

  // ── Order lines management ───────────────────────────────────────────
  const updateLine = (lineData) => {
    setOrderLines(prev => {
      const existing = prev.find(l => l.template_id === lineData.template_id && !l.is_gift);
      if (existing) {
        return prev.map(l => (l.template_id === lineData.template_id && !l.is_gift) ? { ...l, ...lineData } : l);
      }
      return [...prev, lineData];
    });
  };

  // Revert every line of a bundle (same promo_tag) back to its original price
  // — used when the bundle is broken so a single product can never be sold at
  // the bundle price.
  const revertBundle = (lines, tag) => lines
    // drop the bundle's summary strip
    .filter(l => !(l.is_summary && l.promo_tag === tag))
    // and revert the component lines to their original price
    .map(l =>
      (l.promo_tag === tag && !l.is_gift && !l.is_summary)
        ? {
            ...l,
            price_unit: l.orig_price ?? l.price_unit,
            discount: l.orig_discount ?? 0,
            name: l.orig_name ?? l.name,
            promo_tag: undefined, orig_price: undefined,
            orig_discount: undefined, orig_name: undefined, line_uid: undefined,
          }
        : l
    );

  const removeLine = (key) => {
    setOrderLines(prev => {
      const line = prev.find(l => (l.line_uid ?? l.template_id) === key);
      // Removing ANY bundle component cancels the bundle: both products stay at
      // their original prices (the discount is dropped), nothing is deleted.
      if (line && line.promo_tag && !line.is_gift) {
        setTimeout(() => toast('تم إلغاء عرض الباقة — رجعت الأسعار الأصلية'), 0);
        return revertBundle(prev, line.promo_tag);
      }
      // Normal or gift line → just remove it.
      return prev.filter(l => (l.line_uid ?? l.template_id) !== key);
    });
  };

  const handleQtyChange = (key, qty) => {
    setOrderLines(prev => {
      const target = prev.find(l => (l.line_uid ?? l.template_id) === key);
      let next = prev.map(l => (l.line_uid ?? l.template_id) === key ? { ...l, quantity: qty } : l);
      // Changing a bundle component breaks the bundle → cancel its discount.
      if (target && target.promo_tag && !target.is_gift) {
        next = revertBundle(next, target.promo_tag);
        setTimeout(() => toast('تم إلغاء عرض الباقة — رجعت الأسعار الأصلية'), 0);
      }
      // Changing any product qty makes a previously-added gift stale → drop it,
      // the rep re-applies the offer to recompute.
      if (target && !target.is_gift && next.some(l => l.is_gift)) {
        next = next.filter(l => !l.is_gift);
        setTimeout(() => toast('تم حذف الهدية — أعد تطبيق العرض بعد تعديل الكمية'), 0);
      }
      return next;
    });
  };

  const handlePriceChange = (key, price) => {
    setOrderLines(prev => prev.map(l => (l.line_uid ?? l.template_id) === key ? { ...l, price_unit: price } : l));
  };

  const handleDiscountChange = (key, discount) => {
    setOrderLines(prev => prev.map(l => (l.line_uid ?? l.template_id) === key ? { ...l, discount } : l));
  };

  // ── Submit order ─────────────────────────────────────────────────────
  const saveOrder = async (confirm = false) => {
    if (!selectedCustomer) { toast.error('يرجى اختيار عميل'); return; }
    if (orderLines.length === 0) { toast.error('يرجى إضافة منتج واحد على الأقل'); return; }

    // Pre-flight permission checks against the rep profile. The Odoo
    // backend re-enforces these on action_confirm; we duplicate the
    // checks here only for a friendlier UI message.
    if (!isManager) {
      if (!repProfile.allow_create_order) {
        toast.error('ليس لديك صلاحية إنشاء أوامر بيع');
        return;
      }
      if (confirm && !repProfile.allow_confirm_order) {
        toast.error('ليس لديك صلاحية تأكيد أوامر البيع');
        return;
      }
      if (confirm && repProfile.order_confirm_limit > 0) {
        const total = orderLines.reduce((s, l) => {
          const subtotal = (Number(l.quantity) || 0) * (Number(l.price_unit) || 0);
          const disc = (Number(l.discount) || 0) / 100;
          // the server caps the total including VAT (lines carry tax_rate only when it's known)
          return s + subtotal * (1 - disc) * (1 + (Number(l.tax_rate) || 0));
        }, 0);
        if (total > repProfile.order_confirm_limit) {
          toast.error(
            `مبلغ الطلب (${total.toLocaleString('ar-EG')}) تجاوز السقف المسموح به ` +
            `(${Number(repProfile.order_confirm_limit).toLocaleString('ar-EG')} ${cur('ج.م')}). ` +
            'احفظ كمسودة وراجع المشرف.'
          );
          return;
        }
      }
    }

    setSaving(true);
    // Append each applied bundle's summary to the order note so the customer
    // sees it on the printed invoice (the on-screen summary strip itself is not
    // sent to Odoo).
    const promoNotes = orderLines
      .filter(l => l.is_summary)
      .map(l => `🎁 باقة "${l.name}": الأصلي ${Number(l.orig_total).toLocaleString('ar-EG')} ← الباقة ${Number(l.bundle_total).toLocaleString('ar-EG')} ${cur('ج.م')} (وفّرت ${Number(l.saved).toLocaleString('ar-EG')} ${cur('ج.م')})`);
    const fullNote = [notes, ...promoNotes].filter(Boolean).join('\n');

    const createBody = {
      partner_id: selectedCustomer.id,
      lines: orderLines.filter(l => !l.is_summary).map(l => ({
        template_id: l.template_id,
        quantity: l.quantity,
        price_unit: l.price_unit,
        discount: l.discount || 0,
        name: l.name,
      })),
      note: fullNote,
      ...(sellgoDb ? { payment_type: paymentType, date_order: canPickDate ? invoiceDate : todayLocalISO() } : {}),
    };

    // Offline: save the order to the outbox and let it sync later.
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      await enqueue('order', { createBody, confirm });
      notifyEnqueued();
      setSuccessOrder({ name: 'في انتظار المزامنة', __queued: true });
      setSaving(false);
      return;
    }

    try {
      // The draft left by a refused confirmation is replaced by this one.
      if (refusedDraftId) {
        await api.del(`/sales/orders/${refusedDraftId}`).catch(() => {});
        setRefusedDraftId(null);
      }

      // Step 1: create the draft order (the backend never auto-confirms)
      const createRes = await api.post('/sales/orders', createBody);

      if (!createRes?.success) {
        toast.error(createRes?.error || 'فشل إنشاء الطلب');
        setSaving(false);
        return;
      }

      let order = createRes.order || createRes.data;

      // Step 2: optionally confirm via a separate endpoint
      if (confirm && order?.id) {
        let confRes;
        try {
          confRes = await api.post('/sellgo_v5_order_confirm', { order_id: order.id });
        } catch (e) {
          if (!sellgoDb || !(e instanceof ApiError)) throw e;
          if (!e.status) {
            // Connection lost after the draft was saved: don't queue a second copy.
            toast.error('انقطع الاتصال — الطلب محفوظ كمسودة، أكّده من شاشة الطلبات');
            setSuccessOrder(order);
            setSaving(false);
            return;
          }
          // Refused (credit limit, cash not allowed, …): stay here to switch to cash
          // or change the order; the next try replaces this draft.
          setRefusedDraftId(order.id);
          toast.error(e.message);
          setSaving(false);
          return;
        }
        if (confRes?.success) {
          order = confRes.data || order;
        } else {
          // Order is created as draft — surface the confirm error but still show success
          toast.error(confRes?.error || 'تم إنشاء الطلب لكن فشل التأكيد');
        }
      }

      setSuccessOrder(order);
    } catch (e) {
      // Network failure mid-flight → fall back to the offline queue.
      if (e instanceof ApiError && (!e.status || e.status === 0)) {
        await enqueue('order', { createBody, confirm });
        notifyEnqueued();
        setSuccessOrder({ name: 'في انتظار المزامنة', __queued: true });
      } else {
        toast.error(e.message || 'فشل إنشاء الطلب');
      }
    }
    setSaving(false);
  };

  // ── Success screen ───────────────────────────────────────────────────
  if (successOrder) {
    const queued = successOrder.__queued;
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] p-6 gap-6 text-center" dir="rtl">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', damping: 15 }}>
          <div className={`w-24 h-24 rounded-full flex items-center justify-center ${queued ? 'bg-amber-50' : 'bg-emerald-50'}`}>
            <CheckCircle className={`w-14 h-14 ${queued ? 'text-amber-500' : 'text-emerald-500'}`} />
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="space-y-2">
          <h2 className="text-2xl font-bold text-foreground">{queued ? 'تم الحفظ' : 'تم بنجاح!'}</h2>
          {queued ? (
            <p className="text-muted-foreground max-w-xs">
              الطلب اتحفظ على جهازك وهيترفع تلقائياً أول ما النت يرجع.
            </p>
          ) : (
            <>
              <p className="text-muted-foreground">تم إنشاء أمر البيع</p>
              <p className="text-3xl font-bold text-primary">{successOrder.name}</p>
              {successOrder.payment_name && (
                <p className="text-sm text-emerald-600 font-semibold">
                  بيع نقدي — تم تسجيل التحصيل ({successOrder.payment_name})
                </p>
              )}
              {canPickDate && invoiceDate !== todayLocalISO() && (
                <p className="text-sm text-amber-600 font-semibold">بتاريخ {formatDate(`${invoiceDate}T12:00:00`)}</p>
              )}
            </>
          )}
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
          className="grid grid-cols-1 gap-3 w-full max-w-xs">
          <Button
            onClick={() => {
              setSuccessOrder(null); setOrderLines([]); setNotes('');
              setSelectedCustomer(null); setCustomerInfo(null); setStep(1); setPaymentType('credit');
              setInvoiceDate(todayLocalISO());
            }}
            variant="outline" className="rounded-xl h-11"
          >
            طلب جديد
          </Button>
          {!queued && (
            <Button onClick={() => navigate(`/orders/${successOrder.id}`)} className="bg-primary rounded-xl h-11">
              عرض الطلب
            </Button>
          )}
          {selectedCustomer && (
            <Button
              onClick={() => navigate(`/collections/new?customer_id=${selectedCustomer.id}&customer_name=${encodeURIComponent(selectedCustomer.name || '')}`)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-11"
            >
              💵 تحصيل من نفس العميل
            </Button>
          )}
          <Button onClick={() => navigate('/orders')} variant="ghost" className="h-10">
            العودة للطلبات
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" dir="rtl">
      {/* Step indicator */}
      <div className="px-4 pt-4 pb-4 bg-card border-b border-border sticky top-0 z-10">
        <div className="flex items-center justify-center gap-1 mb-3">
          <h1 className="text-base font-bold text-foreground">أمر بيع جديد</h1>
        </div>
        <div className="flex items-start max-w-md mx-auto">
          {STEPS.map((label, i) => {
            const n = i + 1;
            const done = step > n;
            const active = step === n;
            return (
              <Fragment key={i}>
                <div className="flex flex-col items-center gap-1.5" style={{ width: 84 }}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300
                    ${done ? 'bg-primary text-white'
                      : active ? 'bg-primary text-white ring-4 ring-primary/15'
                      : 'bg-muted text-muted-foreground'}`}>
                    {done ? <Check className="w-4 h-4" /> : n}
                  </div>
                  <span className={`text-[11px] font-medium text-center leading-tight ${active ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 rounded-full mt-[18px] transition-colors duration-300 ${done ? 'bg-primary' : 'bg-muted'}`} />
                )}
              </Fragment>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 pb-6">
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <CustomerSelector
                selectedCustomer={selectedCustomer}
                customerInfo={customerInfo}
                onSelect={handleSelectCustomer}
                onContinue={handleCustomerContinue}
              />
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              {/* Customer chip */}
              <div className="flex items-center gap-2 bg-primary/5 border border-primary/20 rounded-xl p-3 mb-4">
                <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-primary text-xs font-bold">{selectedCustomer?.name?.charAt(0)}</span>
                </div>
                <span className="text-sm font-semibold text-foreground flex-1 truncate">{selectedCustomer?.name}</span>
                <button onClick={() => setStep(1)} className="text-xs text-primary font-medium">تغيير</button>
              </div>

              <ProductPicker
                orderLines={orderLines}
                priceMap={priceMap}
                onUpdateLine={updateLine}
                onRemoveLine={removeLine}
                onContinue={() => setStep(3)}
                customerId={selectedCustomer?.id}
              />
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <OrderReview
                customer={selectedCustomer}
                customerInfo={customerInfo}
                orderLines={orderLines}
                onQtyChange={handleQtyChange}
                onPriceChange={handlePriceChange}
                onDiscountChange={handleDiscountChange}
                onRemoveLine={removeLine}
                onChangeCustomer={() => setStep(1)}
                onAddMore={() => setStep(2)}
                onOpenPromos={() => setShowPromos(true)}
                notes={notes}
                onNotesChange={setNotes}
                onSaveDraft={() => saveOrder(false)}
                onConfirm={() => saveOrder(true)}
                saving={saving}
                paymentType={sellgoDb ? paymentType : undefined}
                onPaymentTypeChange={sellgoDb ? setPaymentType : undefined}
                invoiceDate={canPickDate ? invoiceDate : undefined}
                onInvoiceDateChange={canPickDate ? setInvoiceDate : undefined}
              />
              <PromoPicker
                open={showPromos}
                onClose={() => setShowPromos(false)}
                partnerId={selectedCustomer?.id}
                orderLines={orderLines}
                onApplied={(lines, summary) => {
                  let next = lines.filter(l => !l.is_summary);  // drop stale summaries
                  if (summary && summary.promo_tag && summary.saved > 0) {
                    next = [...next, {
                      is_summary: true,
                      promo_tag: summary.promo_tag,
                      line_uid: summary.promo_tag + '_sum',
                      name: summary.promo_name,
                      orig_total: summary.orig_total,
                      bundle_total: summary.bundle_total,
                      saved: summary.saved,
                      quantity: 0, price_unit: 0, discount: 0,
                    }];
                  }
                  setOrderLines(next);
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}